'use client';

import {
  AriaAttributes,
  MouseEventHandler,
  ReactElement,
  forwardRef,
} from 'react';
import { clsx } from 'clsx';

import DallasSvgIcon from '../../Icons/DallasSvgIcon';

import styles from './DallasButton.module.scss';

export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'brand'
  | 'transparent'
  | 'danger';
export type ButtonSize = 'large' | 'regular' | 'small' | 'tiny';

export interface Props extends AriaAttributes {
  /**
   * Button label
   */
  label?: string;
  /**
   * Specifies the color scheme.
   */
  color?: ButtonColor;
  /**
   * Optional className for positioning and identification purposes, button styles will override conflicting styles
   */
  className?: string;
  /**
   * Optional click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Specifies that the corners should be rounded.
   */
  rounded?: boolean;
  /**
   * Specifies that the button should have flex behavior in a flex container.
   */
  flex?: boolean;
  /**
   * Size of the button
   */
  size?: ButtonSize;
  /**
   * Specifies that the button is type="submit", triggering <form onSubmit > event if nested in a form.
   */
  submit?: boolean;
  /**
   * Specifies that the button should be disabled.
   */
  disabled?: boolean;
  /**
   * Optional DallasSvgIcon element to be placed at the start position of the button
   */
  addOnStart?: ReactElement<typeof DallasSvgIcon>;
  /**
   * Optional DallasSvgIcon element to be placed at the end position of the button
   */
  addOnEnd?: ReactElement<typeof DallasSvgIcon>;
  /**
   * Optional tab index
   */
  tabIndex?: number;
  /**
   * Optional id
   */
  id?: string;
}

const DallasButton = forwardRef<HTMLButtonElement, Props>(function DallasButton(
  {
    label,
    onClick,
    className,
    color = 'secondary',
    size = 'regular',
    rounded = false,
    flex = false,
    addOnStart = undefined,
    addOnEnd = undefined,
    disabled = false,
    submit = false,
    tabIndex,
    id,
    ...ariaProps
  }: Props,
  ref,
): ReactElement<typeof DallasButton> {
  const isIconOnly =
    !label && ((!!addOnStart && !addOnEnd) || (!addOnStart && !!addOnEnd));

  return (
    <button
      ref={ref}
      className={clsx(
        className,
        styles.root,
        styles[`color-${color}`],
        flex && styles.flex,
        isIconOnly
          ? styles[`iconOnlyPadding_${size}`]
          : styles[`mixedContentPadding_${size}`],
        rounded ? styles.borderRadius_rounded : styles.borderRadius_semiRounded,
      )}
      onClick={onClick}
      type={submit ? 'submit' : 'button'}
      disabled={disabled}
      tabIndex={tabIndex}
      id={id}
      {...ariaProps}
    >
      {addOnStart && <div className={styles.addOnWrapper}>{addOnStart}</div>}
      {label}
      {addOnEnd && <div className={styles.addOnWrapper}>{addOnEnd}</div>}
    </button>
  );
});

export default DallasButton;
